import { orderBy } from "@progress/kendo-data-query";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getUserCredential } from "src/lib/cookies";
import { useHistory } from "react-router-dom";
import { providerPlanStatementSelector } from "src/redux/selectors/custom-selector";
import {
  setProviderAdvanceFilterDropdownsAction,
  setProviderAdvanceFilterLoadingAction,
  setProviderGroupLoadingAction,
  setProviderGroupsDataAction,
  setProviderKPIPaymentSummaryAction,
  setProviderLoadingAction,
  setProviderPlanDataAction,
  setProviderPlanLoadingAction,
  setProviderPlanPeriodDataAction,
  setProviderPlanPeriodLoadingAction,
  setProviderProvidersDataAction,
  setProviderSection2DataAction,
  setProviderShowSection1,
  setProviderSpecialitiesDataAction,
  setProviderSpecialitiesLoadingAction,
  setProviderSummaryHeaderAction,
  setSelectedProviderCompPlanAction,
  setSelectedProviderGroupsAction,
  setSelectedProviderPeriodAction,
  setSelectedProviderProviderAction,
  setSelectedProviderSpecialitiesAction,
} from "src/redux/actions/providerplan-actions";
import { errStatusHandler, POSTAPI } from "src/api-setup/api-helper";
import { setNewMessage } from "src/redux/actions/setting-actions";
import { GridColumnDataMapper, normalizeSection1Columns, REPORT_SUMMARY_HEADER_TABLE } from "src/utils/utils";
import { GetSystemConfigDataByCode } from "src/lib/common-service";
import { transformDateTime } from "src/lib/date-time.util";

const extractIds = (raw) => {
  if (raw === null || raw === undefined || raw === "") return undefined;
  const arr = Array.isArray(raw) ? raw : [raw];
  const ids = arr
    .map((v) => (v != null && typeof v === "object" ? v.id : v))
    .filter((v) => v !== null && v !== undefined && v !== "" && v !== "Select All" && v !== 0 && v !== "0");
  return ids.length > 0 ? ids.join(",") : undefined;
};

const resultRows = (res) => (Array.isArray(res?.data?.row_result) ? res.data.row_result : []);

const buildFilterParam = (arr, keyField) => {
  const items = arr || [];
  if (!items.length) return undefined;
  if (items.some((it) => Number(it[keyField]) === 0)) return "0";
  return (
    items
      .map((it) => it[keyField])
      .filter(Boolean)
      .join(",") || undefined
  );
};

const useProviderPlanStatementService = () => {
  const cookiesData = getUserCredential();
  const header = {
    "x-access-token": cookiesData.secretToken,
  };
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    selectedCompPlan,
    selectedCompPlanPeriod,
    selectedSpecialities,
    selectedGroups,
    selectedProviders,
    isProviderDetail,
    showReportResults,
    advanceFilterSelections,
  } = useSelector(providerPlanStatementSelector, shallowEqual);

  // When an advance filter (query builder) run is active its selections drive the
  // report; otherwise the outer filter bar values are used. The bar keeps its own
  // defaults either way, matching the Plan Comp Statement report.
  const effectiveCompPlan = advanceFilterSelections?.selectedCompPlan ?? selectedCompPlan;
  const effectivePeriod = advanceFilterSelections?.selectedCompPlanPeriod ?? selectedCompPlanPeriod;
  const effectiveSpecialities = advanceFilterSelections?.selectedSpecialities ?? selectedSpecialities;
  const effectiveGroups = advanceFilterSelections?.selectedGroups ?? selectedGroups;
  const effectiveProviders = advanceFilterSelections?.selectedProviders ?? selectedProviders;
  const effectiveIsProviderDetail =
    advanceFilterSelections !== null ? (advanceFilterSelections?.isProviderDetail ?? false) : isProviderDetail;

  const clearBarAfter = (step) => {
    const steps = ["plan", "period", "specialty", "group", "provider"];
    const start = steps.indexOf(step);
    if (start < 1) {
      dispatch(setProviderPlanPeriodDataAction([]));
      dispatch(setSelectedProviderPeriodAction(null));
    }
    if (start < 2) {
      dispatch(setProviderSpecialitiesDataAction([]));
      dispatch(setSelectedProviderSpecialitiesAction([]));
    }
    if (start < 3) {
      dispatch(setProviderGroupsDataAction([]));
      dispatch(setSelectedProviderGroupsAction([]));
    }
    if (start < 4) {
      dispatch(setProviderProvidersDataAction([]));
      dispatch(setSelectedProviderProviderAction([]));
    }
  };

  const getCompPlan = async () => {
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_user_role_filter: ">0",
      in_user_key: cookiesData.out_user_id,
    };

    dispatch(setProviderPlanLoadingAction(true));

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-comp-plan",
        null,
        header,
        reqData,
        dispatch
      );

      let value = null;
      let data = [];

      const rows = resultRows(res);
      if (res.data.out_parameters.out_ret_code > 0 || rows.length === 0) {
        if (res.data.out_parameters.out_ret_code > 0) errStatusHandler(res, history, dispatch);
        dispatch(setProviderPlanDataAction([]));
        dispatch(setSelectedProviderCompPlanAction(null));
        clearBarAfter("plan");
      } else {
        value = selectedCompPlan || rows[0];
        data = rows;
        dispatch(setProviderPlanDataAction(data));
        dispatch(setSelectedProviderCompPlanAction(value));
      }
    } catch (error) {
      dispatch(setSelectedProviderCompPlanAction(null));
      dispatch(setProviderPlanDataAction([]));
    } finally {
      dispatch(setProviderPlanLoadingAction(false));
    }
  };

  const fetchCompPlanPeriods = async (compPlan) => {
    if (!compPlan) {
      dispatch(setSelectedProviderPeriodAction(null));
      dispatch(setProviderPlanPeriodDataAction([]));
      return;
    }
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_user_role_filter: "abc",
      in_user_key: cookiesData.out_user_id,
      in_comp_plan_key: compPlan?.comp_plan_key,
    };
    dispatch(setProviderPlanPeriodLoadingAction(true));

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-comp-plan-period",
        null,
        header,
        reqData,
        dispatch
      );

      let value = null;
      let data = [];

      const rows = resultRows(res);
      if (res.data.out_parameters.out_ret_code > 0 || rows.length === 0) {
        if (res.data.out_parameters.out_ret_code > 0) errStatusHandler(res, history, dispatch);
        clearBarAfter("period");
      } else {
        if (!showReportResults) {
          value = rows[0];
        } else {
          const filterItem = rows.find((it) => it.period_key === selectedCompPlanPeriod?.period_key);
          value = filterItem || rows[0];
        }

        data = rows;
      }

      dispatch(setProviderPlanPeriodDataAction(data));
      dispatch(setSelectedProviderPeriodAction(value));
    } catch (error) {
      dispatch(setSelectedProviderPeriodAction(null));
      dispatch(setProviderPlanPeriodDataAction([]));
    } finally {
      dispatch(setProviderPlanPeriodLoadingAction(false));
    }
  };

  const fetchSpecialities = async (period, { onSettled } = {}) => {
    if (!period) {
      dispatch(setSelectedProviderSpecialitiesAction([]));
      dispatch(setProviderSpecialitiesDataAction([]));
      onSettled?.();
      return;
    }
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_comp_plan_key: selectedCompPlan?.comp_plan_key,
      in_period_key: period?.period_key,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
    };
    dispatch(setSelectedProviderGroupsAction([]));
    dispatch(setProviderGroupsDataAction([]));
    dispatch(setSelectedProviderProviderAction([]));
    dispatch(setProviderProvidersDataAction([]));
    dispatch(setProviderSpecialitiesLoadingAction(true));

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-specialty",
        null,
        header,
        reqData,
        dispatch
      );

      let data = [];
      let value = [];

      const rows = resultRows(res);
      const retCode = Number(res?.data?.out_parameters?.out_ret_code ?? 0);
      if (retCode > 0) {
        errStatusHandler(res, history, dispatch);
      }
      if (retCode > 0 || rows.length === 0) {
        data = [];
        value = [];
      } else {
        data = [{ specialty_key: 0, specialty_name: "Select All" }, ...rows];
        if (!showReportResults) {
          value = [{ specialty_key: 0, specialty_name: "Select All" }];
        } else {
          const selectedKeys = (selectedSpecialities || []).map((it) => it.specialty_key);
          const filteredArray = rows.filter((row) => selectedKeys.includes(row.specialty_key));
          value = filteredArray.length > 0 ? [...selectedSpecialities] : [data[0]];
        }
      }

      dispatch(setProviderSpecialitiesDataAction(data));
      dispatch(setSelectedProviderSpecialitiesAction(value));
      if (retCode > 0) {
        return;
      }
      await fetchGroups(value, { period, compPlan: selectedCompPlan });
    } catch (error) {
      dispatch(setProviderSpecialitiesDataAction([]));
      dispatch(setSelectedProviderSpecialitiesAction([]));
    } finally {
      onSettled?.();
      dispatch(setProviderSpecialitiesLoadingAction(false));
    }
  };

  const fetchGroups = async (specialities, { onSettled, period, compPlan } = {}) => {
    const periodRow = period || selectedCompPlanPeriod;
    const planRow = compPlan || selectedCompPlan;
    const specialtySelection = specialities?.length
      ? specialities
      : [{ specialty_key: 0, specialty_name: "Select All" }];
    if (!periodRow) {
      dispatch(setSelectedProviderGroupsAction([]));
      dispatch(setProviderGroupsDataAction([]));
      onSettled?.();
      return;
    }
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_comp_plan_key: planRow?.comp_plan_key,
      in_period_key: periodRow?.period_key,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_specialty_key: specialtySelection.map((it) => it.specialty_key).join(","),
    };
    dispatch(setSelectedProviderProviderAction([]));
    dispatch(setProviderProvidersDataAction([]));
    dispatch(setProviderGroupLoadingAction(true));

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-group",
        null,
        header,
        reqData,
        dispatch
      );

      let value = [];
      let data = [];

      const rows = resultRows(res);
      const retCode = Number(res?.data?.out_parameters?.out_ret_code ?? 0);
      if (retCode > 0) {
        errStatusHandler(res, history, dispatch);
      }
      if (retCode > 0 || rows.length === 0) {
        data = [];
        value = [];
      } else {
        data = [{ title_key: 0, title_name: "Select All" }, ...rows];
        if (!showReportResults) {
          value = [data[0]];
        } else {
          const selectedKeys = (selectedGroups || []).map((it) => it.title_key);
          const filteredArray = rows.filter((row) => selectedKeys.includes(row.title_key));
          value = filteredArray.length > 0 ? filteredArray : [data[0]];
        }
      }

      dispatch(setProviderGroupsDataAction(data));
      dispatch(setSelectedProviderGroupsAction(value));
      if (retCode > 0) {
        return;
      }
      await fetchProviders({ groups: value, specialities: specialtySelection, period: periodRow, compPlan: planRow });
    } catch (error) {
      dispatch(setProviderGroupsDataAction([]));
      dispatch(setSelectedProviderGroupsAction([]));
    } finally {
      onSettled?.();
      dispatch(setProviderGroupLoadingAction(false));
    }
  };

  const getAutoHideSection1 = async () => {
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      pref_code: ["auto_hide_report_sec1"],
    };
    let res = await GetSystemConfigDataByCode(reqData, history, dispatch);
    if (res) {
      let flag = res.auto_hide_report_sec1 === "0" ? true : false;
      dispatch(setProviderShowSection1(flag));
    }
  };

  const fetchProviders = async (context) => {
    if (context === null) {
      dispatch(setSelectedProviderProviderAction([]));
      dispatch(setProviderProvidersDataAction([]));
      return null;
    }
    const compPlanKey =
      extractIds(context?.comp_plan_key) ?? context?.compPlan?.comp_plan_key ?? selectedCompPlan?.comp_plan_key;
    const periodKey =
      extractIds(context?.period_key) ?? context?.period?.period_key ?? selectedCompPlanPeriod?.period_key;
    const specialtyKeys =
      extractIds(context?.specialty_key) ??
      buildFilterParam(context?.specialities || selectedSpecialities || [], "specialty_key") ??
      "0";
    const groupKeys =
      extractIds(context?.title_key) ?? buildFilterParam(context?.groups || selectedGroups || [], "title_key") ?? "0";
    if (!groupKeys) {
      dispatch(setSelectedProviderProviderAction([]));
      dispatch(setProviderProvidersDataAction([]));
      return;
    }
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_comp_plan_key: compPlanKey,
      in_period_key: periodKey,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_user_role_filter: "> 0",
      in_specialty_key: specialtyKeys,
      in_group_key: groupKeys,
      in_encrypt_key: cookiesData.out_acct_encrypt_key,
    };
    dispatch(setProviderLoadingAction(true));
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-provider",
        null,
        header,
        reqData,
        dispatch
      );
      let data = [];
      let value = [];
      const rows = resultRows(res);
      const retCode = Number(res?.data?.out_parameters?.out_ret_code ?? 0);
      if (retCode > 0) {
        errStatusHandler(res, history, dispatch);
      }
      if (retCode === 0 && rows.length > 0) {
        data = [{ provider_key: 0, provider_name: "Select All" }, ...rows];
        if (!showReportResults) {
          value = [data[0]];
        } else {
          const selectedKeys = selectedProviders.map((it) => it.provider_key);
          const filteredArray = rows.filter((row) => selectedKeys.includes(row.provider_key));
          value = filteredArray.length > 0 ? filteredArray : [data[0]];
        }
        if (cookiesData.out_db_role_type_abbr === "prv1") {
          value = [rows[0]];
          data = data.filter((it) => it.provider_key !== 0);
        }
      }
      dispatch(setProviderProvidersDataAction(data));
      dispatch(setSelectedProviderProviderAction(value));
      return res.data.row_result[0]?.provider_key ?? null;
    } catch (error) {
      dispatch(setSelectedProviderProviderAction([]));
      dispatch(setProviderProvidersDataAction([]));
      return null;
    } finally {
      dispatch(setProviderLoadingAction(false));
    }
  };

  const fetchSummaryHeaderProvider = async (payload = {}) => {
    const specialtyKeys = buildFilterParam(effectiveSpecialities, "specialty_key") ?? "0";
    const groupKeys = buildFilterParam(effectiveGroups, "title_key") ?? "0";
    const providerKeys = buildFilterParam(effectiveProviders, "provider_key") ?? "0";
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_comp_plan_key: effectiveCompPlan?.comp_plan_key,
      in_period_key: effectivePeriod?.period_key,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_specialty_keys: specialtyKeys,
      in_group_keys: groupKeys,
      in_provider_keys: providerKeys,
      in_encrypt_key: cookiesData.out_acct_encrypt_key,
      in_sort_column: payload.in_sort_column ?? null,
      in_sort_order: payload.in_sort_order ?? "ASC",
    };

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-provider-details",
        null,
        header,
        reqData,
        dispatch
      );
      let data = [];
      let columns = [];

      if (res.data.out_parameters.out_columns_xml) {
        columns = normalizeSection1Columns(
          GridColumnDataMapper(res.data.out_parameters.out_columns_xml, null, true).map((column) => ({
            ...column,
            gname: column.gname || REPORT_SUMMARY_HEADER_TABLE,
          }))
        );
      }

      if (res.data.out_parameters.out_ret_code > 0) {
        dispatch(
          setNewMessage({
            message: res.data.out_parameters.out_error_msg,
            type: "Error",
            show: true,
          })
        );
        errStatusHandler(res, history, dispatch);
      }

      data = (res.data.row_result || []).map((it) => {
        if (it.hire_date) {
          return {
            ...it,
            hire_date: transformDateTime({
              date: it.hire_date,
              format: "MM/DD/YYYY",
            }),
          };
        }
        return it;
      });
      dispatch(setProviderSummaryHeaderAction({ columns, data: data }));
    } catch (error) {}
  };

  const compDetailUpdateColumnOrder = async (columnString) => {
    const reqData = {
      in_db_name: "procare_system",
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter_logical_cond: "Y",
      in_filter_label_cond: "N",
      in_column_info: columnString,
    };

    try {
      const res = await POSTAPI(false, "/reports/comp-detail-update-column-order", null, header, reqData, dispatch);
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const fetchProviderKpiPaymentSummary = async (payload) => {
    const specialtyKeys = buildFilterParam(effectiveSpecialities, "specialty_key") ?? "0";
    const groupKeys = buildFilterParam(effectiveGroups, "title_key") ?? "0";
    const providerKeys = buildFilterParam(effectiveProviders, "provider_key") ?? "0";
    let reqData = {
      in_filter: payload.in_filter,
      in_sort_number: payload.in_sort_number,
      in_sort_order: payload.in_sort_order,
      in_tenant_id: cookiesData.out_tenant_id,
      in_comp_plan_key: effectiveCompPlan?.comp_plan_key,
      in_period_key: effectivePeriod?.period_key,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_specialty_keys: specialtyKeys,
      in_group_keys: groupKeys,
      in_provider_keys: providerKeys,
      in_encrypt_key: cookiesData.out_acct_encrypt_key,
    };

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-kpi-payment-summary",
        null,
        header,
        reqData,
        dispatch
      );
      let data = [];
      let columns = [];

      if (res.data.out_parameters.out_columns_xml) {
        columns = GridColumnDataMapper(res.data.out_parameters.out_columns_xml, null);
      }
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
      }
      data = res.data.row_result;
      dispatch(setProviderKPIPaymentSummaryAction({ columns, data: data }));
    } catch (error) {}
  };

  const fetchReportSummaryColumnHeaders = async () => {
    let reqData = {
      in_period_key: effectivePeriod?.period_key,
    };
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-report-unpub-summary-column-headers",
        null,
        header,
        reqData,
        dispatch
      );
      let data = [];

      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
      }
      data = res.data.row_result.map((it) => ({ ...it, show: true }));
      dispatch(setProviderSection2DataAction({ columns: data }));
    } catch (error) {}
  };

  const fetchProviderKpiPaymentDetails = async () => {
    const specialtyKeys = buildFilterParam(effectiveSpecialities, "specialty_key") ?? "0";
    const groupKeys = buildFilterParam(effectiveGroups, "title_key") ?? "0";
    const providerKeys = buildFilterParam(effectiveProviders, "provider_key") ?? "0";
    let reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_comp_plan_key: effectiveCompPlan?.comp_plan_key,
      in_period_key: effectivePeriod?.period_key,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_specialty_keys: specialtyKeys,
      in_group_keys: groupKeys,
      in_provider_keys: providerKeys,
      in_encrypt_key: cookiesData.out_acct_encrypt_key,
      in_aggregate_flag: effectiveIsProviderDetail ? 0 : 1,
    };

    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-kpi-payment-details",
        null,
        header,
        reqData,
        dispatch
      );
      let data = [];

      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
      }
      // data = res.data.row_result;
      data = res.data.row_result.map((it) => {
        const newObj = { ...it };
        newObj.period1_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period1_value) : 0;
        newObj.period2_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period2_value) : 0;
        newObj.period3_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period3_value) : 0;
        newObj.period4_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period4_value) : 0;
        newObj.period5_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period5_value) : 0;
        newObj.period6_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period6_value) : 0;
        newObj.period7_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period7_value) : 0;
        newObj.period8_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period8_value) : 0;
        newObj.period9_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period9_value) : 0;
        newObj.period10_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period10_value) : 0;
        newObj.period11_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period11_value) : 0;
        newObj.period12_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period12_value) : 0;
        newObj.period13_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period13_value) : 0;
        newObj.period14_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period14_value) : 0;
        newObj.period15_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period15_value) : 0;
        newObj.period16_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period16_value) : 0;
        newObj.period17_value_total = it.result_metadata_tags === "<grand_total>" ? Number(it.period17_value) : 0;

        return newObj;
      });
      const resultdata = orderBy(data, [
        { field: "kpi_report_order", dir: "asc" },
        { field: "kpi_name", dir: "asc" },
        { field: "result_name_report_order", dir: "asc" },
        { field: "result_name", dir: "asc" },
      ]);

      dispatch(setProviderSection2DataAction({ data: resultdata }));
    } catch (error) {}
  };

  const fetchAdvFilterCompPlan = async (cascadeContext = {}, { isStale } = {}) => {
    dispatch(setProviderAdvanceFilterLoadingAction({ compPlan: true }));
    const periodKey = extractIds(cascadeContext?.period_key);
    const specialityKeys = extractIds(cascadeContext?.specialty_key);
    const groupKeys = extractIds(cascadeContext?.title_key);
    const providerKeys = extractIds(cascadeContext?.provider_key);
    const reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_user_role_filter: ">0",
      in_user_key: cookiesData.out_user_id,
      in_period_key: periodKey,
      in_specialty_key: specialityKeys,
      in_group_key: groupKeys,
      in_provider_key: providerKeys,
    };
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-comp-plan",
        null,
        header,
        reqData,
        dispatch
      );
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
        return null;
      }
      const data = resultRows(res);
      if (isStale?.()) return null;
      dispatch(setProviderAdvanceFilterDropdownsAction({ compPlan: data }));
      const mappedData = data.map((it) => ({
        text: it.comp_plan_name,
        id: String(it.comp_plan_key),
        dataItemKey: "comp_plan_key",
      }));
      return { firstKey: data[0]?.comp_plan_key ?? null, mappedData };
    } catch {
      return null;
    } finally {
      dispatch(setProviderAdvanceFilterLoadingAction({ compPlan: false }));
    }
  };

  const fetchAdvFilterPeriods = async (cascadeContext = {}, { isStale } = {}) => {
    dispatch(setProviderAdvanceFilterLoadingAction({ period: true }));
    const compPlanKey = extractIds(cascadeContext?.comp_plan_key);
    const specialityKeys = extractIds(cascadeContext?.specialty_key);
    const groupKeys = extractIds(cascadeContext?.title_key);
    const providerKeys = extractIds(cascadeContext?.provider_key);
    const reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_user_role_filter: "abc",
      in_user_key: cookiesData.out_user_id,
      in_comp_plan_key: compPlanKey,
      in_specialty_key: specialityKeys,
      in_group_key: groupKeys,
      in_provider_key: providerKeys,
    };
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-comp-plan-period",
        null,
        header,
        reqData,
        dispatch
      );
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
        return null;
      }
      const data = resultRows(res);
      if (isStale?.()) return null;
      dispatch(setProviderAdvanceFilterDropdownsAction({ period: data }));
      const mappedData = data.map((it) => ({
        text: it.period_name,
        id: String(it.period_key),
        dataItemKey: "period_key",
      }));
      return { firstKey: data[0]?.period_key ?? null, mappedData };
    } catch {
      return null;
    } finally {
      if (!isStale?.()) dispatch(setProviderAdvanceFilterLoadingAction({ period: false }));
    }
  };

  const fetchAdvFilterSpecialities = async (cascadeContext = {}, { isStale } = {}) => {
    dispatch(setProviderAdvanceFilterLoadingAction({ speciality: true }));
    const compPlanKey = extractIds(cascadeContext?.comp_plan_key);
    const periodKey = extractIds(cascadeContext?.period_key);
    const groupKeys = extractIds(cascadeContext?.title_key);
    const providerKeys = extractIds(cascadeContext?.provider_key);
    const reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_comp_plan_key: compPlanKey,
      in_period_key: periodKey,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_group_key: groupKeys,
      in_provider_key: providerKeys,
    };
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-specialty",
        null,
        header,
        reqData,
        dispatch
      );
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
        return null;
      }
      const rows = resultRows(res);
      const data = rows.length > 0 ? [{ specialty_key: 0, specialty_name: "Select All" }, ...rows] : [];
      if (isStale?.()) return null;
      dispatch(setProviderAdvanceFilterDropdownsAction({ speciality: data }));
      const mappedData = rows.map((it) => ({
        text: it.specialty_name,
        id: String(it.specialty_key),
        dataItemKey: "specialty_key",
      }));
      return { firstKey: rows[0]?.specialty_key ?? null, mappedData };
    } catch {
      return null;
    } finally {
      if (!isStale?.()) dispatch(setProviderAdvanceFilterLoadingAction({ speciality: false }));
    }
  };

  const fetchAdvFilterGroups = async (cascadeContext = {}, { isStale } = {}) => {
    dispatch(setProviderAdvanceFilterLoadingAction({ group: true }));
    const compPlanKey = extractIds(cascadeContext?.comp_plan_key);
    const periodKey = extractIds(cascadeContext?.period_key);
    const specialityKeys = extractIds(cascadeContext?.specialty_key);
    const providerKeys = extractIds(cascadeContext?.provider_key);
    const reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_comp_plan_key: compPlanKey,
      in_period_key: periodKey,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_specialty_key: specialityKeys,
      in_provider_key: providerKeys,
    };
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-group",
        null,
        header,
        reqData,
        dispatch
      );
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
        return null;
      }
      const rows = resultRows(res);
      const data = rows.length > 0 ? [{ title_key: 0, title_name: "Select All" }, ...rows] : [];
      if (isStale?.()) return null;
      dispatch(setProviderAdvanceFilterDropdownsAction({ group: data }));
      const mappedData = rows.map((it) => ({
        text: it.title_name,
        id: String(it.title_key),
        dataItemKey: "title_key",
      }));
      return { firstKey: rows[0]?.title_key ?? null, mappedData, data };
    } catch {
      return null;
    } finally {
      if (!isStale?.()) dispatch(setProviderAdvanceFilterLoadingAction({ group: false }));
    }
  };

  const fetchAdvFilterProviders = async (cascadeContext = {}, { isStale } = {}) => {
    dispatch(setProviderAdvanceFilterLoadingAction({ provider: true }));
    const compPlanKey = extractIds(cascadeContext?.comp_plan_key);
    const periodKey = extractIds(cascadeContext?.period_key);
    const specialityKeys = extractIds(cascadeContext?.specialty_key);
    const groupKeys = extractIds(cascadeContext?.title_key);
    const reqData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_comp_plan_key: compPlanKey,
      in_period_key: periodKey,
      in_user_key: cookiesData.out_user_id,
      in_user_role_type_abbr: cookiesData.out_db_role_type_abbr,
      in_user_role_filter: "> 0",
      in_specialty_key: specialityKeys,
      in_group_key: groupKeys,
      in_encrypt_key: cookiesData.out_acct_encrypt_key,
    };
    try {
      const res = await POSTAPI(
        false,
        "/reports/select-provider-comp-report-filter-provider",
        null,
        header,
        reqData,
        dispatch
      );
      if (res.data.out_parameters.out_ret_code > 0) {
        errStatusHandler(res, history, dispatch);
        return null;
      }
      const rows = resultRows(res);
      const data = rows.length > 0 ? [{ provider_key: 0, provider_name: "Select All" }, ...rows] : [];
      if (isStale?.()) return null;
      dispatch(setProviderAdvanceFilterDropdownsAction({ provider: data }));
      const mappedData = rows.map((it) => ({
        text: it.provider_name,
        id: String(it.provider_key),
        dataItemKey: "provider_key",
      }));
      return { firstKey: rows[0]?.provider_key ?? null, mappedData, data };
    } catch {
      return null;
    } finally {
      if (!isStale?.()) dispatch(setProviderAdvanceFilterLoadingAction({ provider: false }));
    }
  };

  return {
    getCompPlan,
    fetchCompPlanPeriods,
    fetchSpecialities,
    fetchGroups,
    fetchProviders,
    fetchAdvFilterCompPlan,
    fetchAdvFilterPeriods,
    fetchAdvFilterSpecialities,
    fetchAdvFilterGroups,
    fetchAdvFilterProviders,
    getAutoHideSection1,
    fetchSummaryHeaderProvider,
    fetchProviderKpiPaymentSummary,
    fetchReportSummaryColumnHeaders,
    fetchProviderKpiPaymentDetails,
    compDetailUpdateColumnOrder,
  };
};

export default useProviderPlanStatementService;
